import { motion } from "framer-motion";
import { Eye, Lock, Scale, ShieldAlert, CheckCircle2 } from "lucide-react";
import { fadeUp, stagger, viewport, easeOut } from "@/lib/motion";
import { AccessVisual } from "@/components/Visuals";

const CHAPTERS = [
  {
    n: "01",
    title: "The Gap",
    body: "AI tools are becoming increasingly accessible, but turning them into practical value remains a challenge. Many businesses and individuals still struggle to understand where AI can help, how to apply it effectively, and how to use it in ways relevant to their specific needs.",
  },
  {
    n: "02",
    title: "What We Believe",
    body: "AI should not be limited to those with technical expertise or significant resources. It should help people and businesses navigate complexity, access relevant insights, and make more informed choices with greater confidence.",
  },
  {
    n: "03",
    title: "The Change We Want to Make",
    body: "We want to bridge the gap between what AI can do and how people and businesses can meaningfully use it — not by making everyone an AI expert, but by designing AI around real needs, real decisions and real experiences.",
  },
];

const BELIEFS = [
  "AI should work in service of people — not the other way around.",
  "Powerful technology doesn't need to be complicated to be valuable.",
  "Access to AI's benefits shouldn't depend on your budget, background, or the size of your technical team.",
];

const CORE = [
  { icon: Eye, title: "Human oversight", body: "AI supports decisions; it never replaces human judgment." },
  { icon: Lock, title: "Privacy & data responsibility", body: "Your data is handled with care, transparency, and respect." },
  { icon: CheckCircle2, title: "Transparency", body: "We're clear about what our tools do, how they work, and where their limits are." },
];

const ALSO = [
  { icon: Scale, title: "Fairness", body: "Designed to minimize bias, and avoid outcomes that disadvantage people unfairly." },
  { icon: ShieldAlert, title: "Security", body: "Built in from the start, not bolted on afterward." },
  { icon: CheckCircle2, title: "Appropriate use", body: "We recommend AI where it adds real value — and say so when it doesn't." },
];

const WhyWeExist = () => {
  return (
    <section id="why" className="relative py-28 lg:py-36 border-t border-black/5">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
          <span className="badge-pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
            Why We Exist
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-4xl leading-[1.1]">
            The true potential of AI isn't what the technology can do — it's <span className="text-[#43B0CE]">how people apply it.</span>
          </h2>
          <p className="mt-6 max-w-3xl text-lg font-light leading-relaxed text-[#3C5156]">
            AI is one of the most powerful technologies of our time. Yet its real value is in how people and
            businesses can apply it to solve real problems, gain meaningful insights, and make better-informed
            decisions. The opportunity is not simply access to AI — it is making AI practical, purposeful and useful.
          </p>
        </motion.div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-px">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: easeOut }}
                className="group border-t border-black/10 py-8 grid grid-cols-[auto_1fr] gap-6 hover:bg-black/[0.02] transition-colors px-2"
              >
                <span className="font-mono-ed w-fit rounded-full border border-[#43B0CE]/35 px-2.5 py-1 text-[11px] tracking-[0.18em] text-[#43B0CE]">{c.n}</span>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#0B2429] group-hover:text-[#7FD0E6] transition-colors">{c.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#3C5156]">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: easeOut }}
              className="sticky top-28 rounded-3xl overflow-hidden border border-black/10 bg-[#FFFFFF]"
            >
              <AccessVisual />
              <div className="bg-[#FFFFFF] p-8">
                <span className="font-display text-5xl font-extrabold leading-none text-[#43B0CE]">{"\u201C"}</span>
                <p className="mt-1 font-quote text-xl sm:text-2xl font-medium leading-snug text-[#0B2429]">
                  We exist to make AI <span className="text-[#43B0CE]">usefully accessible and purposeful</span> — so
                  more people and businesses can benefit from it in meaningful ways.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {BELIEFS.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="rounded-2xl border border-black/10 bg-gradient-to-b from-black/[0.05] to-transparent p-7"
            >
              <div className="h-1 w-10 bg-[#43B0CE] mb-5 rounded-full" />
              <p className="text-[15px] leading-relaxed text-[#3C5156]">{b}</p>
            </motion.div>
          ))}
        </div>

        {/* Responsible AI — part of what we believe */}
        <div className="mt-20">
          <p className="max-w-3xl font-quote text-xl text-[#3C5156]">
            We believe responsible AI isn't a constraint on innovation — it's what makes innovation worth trusting.
          </p>

          <p className="mt-8 font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#43B0CE]">Our core commitments</p>
          <motion.div variants={stagger()} initial="hidden" whileInView="show" viewport={viewport} className="mt-4 grid md:grid-cols-3 gap-5">
            {CORE.map((c) => (
              <motion.div key={c.title} variants={fadeUp} className="rounded-2xl border border-[#43B0CE]/25 bg-gradient-to-b from-[#43B0CE]/[0.08] to-transparent p-8">
                <c.icon className="h-7 w-7 text-[#43B0CE]" />
                <h4 className="mt-5 font-display text-lg font-semibold text-[#0B2429]">{c.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#3C5156]">{c.body}</p>
              </motion.div>
            ))}
          </motion.div>

          <p className="mt-8 font-mono-ed text-[10px] tracking-[0.2em] uppercase text-[#64787E]">Also central to how we work</p>
          <motion.div variants={stagger()} initial="hidden" whileInView="show" viewport={viewport} className="mt-4 grid md:grid-cols-3 gap-4">
            {ALSO.map((c) => (
              <motion.div key={c.title} variants={fadeUp} className="rounded-xl border border-black/10 bg-[#FFFFFF] p-5 flex items-start gap-3">
                <c.icon className="h-5 w-5 text-[#64787E] mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-display text-base font-semibold text-[#0B2429]">{c.title}</h4>
                  <p className="mt-1 text-sm leading-relaxed text-[#64787E]">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyWeExist;
